import axios from "axios";
import {browserSessionStorage} from "core/browserStorage";
import {CURRENT_LADDER_OFFER_PATH, currentLadderSendPath, offerLadderOfferPath} from "api/endpointPaths.mjs";
import type {PricingCountry} from "pricing/planCatalogue";
import type {LadderOffer, LadderResolution} from "./ladderOffer";
import {CURRENT_LADDER_REFERENCE_PREFIX, currentLadderSendId, type CurrentLadderOffer, type CurrentLadderResolution} from "./currentLadderOffer";

/** Email capabilities remain anonymous; current.* references always use authenticated account routes. */
export class LadderOfferApi {
	/** Plain read. Neither route records a click merely because the pricing page mounted. */
	static async resolve(token: string, country: PricingCountry | null = null): Promise<LadderResolution> {
		const sendId = currentLadderSendId(token);
		if (token.startsWith(CURRENT_LADDER_REFERENCE_PREFIX) && sendId === null) return {status: "refused"};
		try {
			const {data} = await axios.get<LadderOffer>(
				sendId === null ? offerLadderOfferPath(token) : `${currentLadderSendPath(sendId)}/offer`,
				{params: country ? {country} : {}});
			return {status: "offer", offer: data};
		} catch (error) {
			const status = httpStatusOf(error);
			// An edge/limiter failure says nothing about the offer's expiry.
			return status === 400 || status === 404 ? {status: "refused"} : {status: "unavailable"};
		}
	}

	private static readonly issued = new Set<string>();
	private static readonly pending = new Map<string, Promise<CurrentLadderResolution>>();

	/** Remember the attempt before dispatch: even an ambiguous network failure must not issue twice. */
	static async currentOffer(country: PricingCountry | null = null, transferId: string | null = null,
		accountKey: string | null = null): Promise<CurrentLadderResolution> {
		const key = accountKey && transferId ? `offers.couponIssue.v1.${accountKey}.${transferId}` : null;
		if (key !== null && this.pending.has(key)) return this.pending.get(key)!;
		let issue = key !== null && !this.issued.has(key);
		if (key !== null) {
			try {
				if (browserSessionStorage()?.getItem(key) === "1") issue = false;
				browserSessionStorage()?.setItem(key, "1");
			} catch {
				// The in-memory record still prevents repeats when browser storage is unavailable.
			}
			this.issued.add(key);
		}
		const params = {...(country ? {country} : {}), ...(issue ? {transferId} : {})};
		const request = axios.get<CurrentLadderOffer>(CURRENT_LADDER_OFFER_PATH, {params})
			.then(({status, data}): CurrentLadderResolution => status === 204 ? {status: "none"} : {status: "offer", offer: data})
			.catch((): CurrentLadderResolution => ({status: "unavailable"}));
		if (key !== null) this.pending.set(key, request);
		try { return await request; }
		finally { if (key !== null) this.pending.delete(key); }
	}

	/** Reports are separate from reads so an unselected candidate does not count as a showing. */
	static async report(sendId: string, step: "shown" | "click"): Promise<"recorded" | "refused" | "unavailable"> {
		try {
			await axios.post(`${currentLadderSendPath(sendId)}/${step}`);
			return "recorded";
		} catch (error) {
			return httpStatusOf(error) === 404 ? "refused" : "unavailable";
		}
	}
}

/** Structural read also accepts an error originating in a second copy of axios. */
export function httpStatusOf(error: unknown): number | null {
	const status = (error as {response?: {status?: unknown}} | null)?.response?.status;
	return typeof status === "number" ? status : null;
}
